import type {
  Duration, Label, Onset, Pixel,
} from '@/data';
import { Cell, Line } from '@/data';
import type { Image3DPageFormat, Image3DRowFormat, Image3DSyncFormat } from '@/formats/UnitParser';

export const pixelByOnset = 100 as Pixel;

export function createCell(label: Label, nextOnset: Onset): Cell {
  const { onset } = label;
  const duration = (nextOnset - onset) as Duration;
  return new Cell(duration, onset);
}

export function createCells(labels: Label[], pieceOnsetLength: Onset): Cell[] {
  return labels.map((l: Label, index, arrayLabels) => {
    const nextOnset: Onset = arrayLabels[index + 1]?.onset || pieceOnsetLength;
    return createCell(l, nextOnset);
  });
}

function generateOnsetX(cell: Cell) {
  const endOfCell = cell.onset + cell.duration;
  return {
    onset: endOfCell,
    x: (endOfCell) * pixelByOnset,
  };
}

function addFirstOnsetX(onsetXList: { onset: number; x: number; }[], firstOnsetForTheRow: number) {
  onsetXList.unshift({
    onset: firstOnsetForTheRow,
    x: firstOnsetForTheRow * pixelByOnset,
  });
}

function alignOnsetXToTheLeft(onsetX: { onset: number; x: number; }, firstOnsetForTheRow: number) {
  return {
    onset: onsetX.onset,
    x: onsetX.x - firstOnsetForTheRow * pixelByOnset,
  };
}

function generateOnsetXList(line: Line) {
  const firstOnsetForTheRow = line.cells[0].onset;

  const onsetXList = line.cells.map(generateOnsetX);

  addFirstOnsetX(onsetXList, firstOnsetForTheRow);

  const alignedOnsetXList = onsetXList
    .map((onsetX) => alignOnsetXToTheLeft(onsetX, firstOnsetForTheRow));

  return alignedOnsetXList;
}

function generateRow(line: Line, rowHeight: Pixel, rowIndex: number): Image3DRowFormat {
  const onsetXList = generateOnsetXList(line);

  const row: Image3DRowFormat = {
    'onset-x': onsetXList,
    height: rowHeight,
    y: rowIndex * rowHeight,
    'start-x': 0,
    'end-x': line.getDuration() * pixelByOnset,
  };
  return row;
}

export function generatePositions(
  lines: Line[],
  rowHeight: Pixel = 100 as Pixel,
): Image3DSyncFormat {
  const rows: Image3DRowFormat[] = lines
    .map((line, rowIndex) => generateRow(line, rowHeight, rowIndex));

  const page: Image3DPageFormat = { rows, image: 'undefined' };

  return { pages: [page] } as Image3DSyncFormat;
}

export function getGeneratedPositions(rowHeight: Pixel): Image3DSyncFormat {
  const lines = [
    new Line([
      new Cell(4, 0),
      new Cell(4, 4),
      new Cell(2, 8),
      new Cell(4, 10),

    ]),
    new Line([
      new Cell(2, 14),
      new Cell(2, 16),
      new Cell(2, 18),
      new Cell(4, 20),
    ]),
    new Line([
      new Cell(2, 24),
      new Cell(3, 26),
      new Cell(3, 29),
      new Cell(4, 32),
    ]),
    new Line([
      new Cell(5, 36),
      new Cell(4, 41),
      new Cell(3, 45),
    ]),
  ];
  return generatePositions(lines, rowHeight);
}
